import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { Page, Loader } from "../components/UI";
import { toast } from "sonner";

export default function AdminSeasonsPage() {
  const [seasons, setSeasons] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", code: "" });

  const load = () => {
    api.get("/season/list").then(({ data }) => setSeasons(data.seasons));
    api.get("/season/tiers").then(({ data }) => setTiers(data.tiers));
  };
  useEffect(load, []);

  const active = (seasons || []).find((s) => s.race_status === "ACTIVE");

  const create = async () => {
    try {
      await api.post("/admin/season/create", form);
      toast.success("Season created"); setForm({ name: "", code: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const act = async (id, action) => {
    try { await api.post(`/admin/season/${id}/${action}`); toast.success(`Season ${action}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const searchLedger = async () => {
    if (!active) { toast.error("No active season"); return; }
    try {
      const { data } = await api.get(`/admin/season/${active.id}/ledger`, { params: { q } });
      setLedger(data.ledger);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const revoke = async (rid) => {
    try { await api.post(`/admin/season/results/${rid}/revoke`, { reason: "Admin revoke" }); toast.success("Revoked"); searchLedger(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const recalc = async () => {
    if (!active) return;
    try { await api.post(`/admin/season/${active.id}/recalculate`); toast.success("Recalculated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!seasons) return <Loader />;

  return (
    <Page>
      <h1 className="font-heading text-4xl font-black uppercase text-slate-50 mb-6">Admin · Seasons</h1>

      <section className="rounded-xl border border-[#2b2512] bg-[#0a0a0a] p-4 mb-6">
        <h2 className="text-lg font-bold text-slate-100 mb-3">Seasons</h2>
        <div className="space-y-2" data-testid="admin-seasons-list">
          {seasons.map((s) => (
            <div key={s.id} data-testid={`admin-season-${s.id}`} className="flex items-center justify-between rounded-lg bg-[#070707] border border-[#14110a] px-3 py-2">
              <div>
                <div className="text-slate-100 font-semibold">{s.name} <span className="text-xs font-mono text-slate-500">({s.code || "—"})</span></div>
                <div className="text-xs font-mono uppercase tracking-wide text-[#E5B842]">{s.race_status || s.status}</div>
              </div>
              <div className="flex gap-2">
                {s.race_status !== "ACTIVE" && s.race_status !== "CLOSED" && (
                  <button data-testid={`activate-${s.id}`} onClick={() => act(s.id, "activate")} className="px-3 py-1 rounded-md bg-[#E5B842] text-[#000000] text-xs font-bold">Activate</button>
                )}
                {s.race_status === "ACTIVE" && (
                  <button data-testid={`close-${s.id}`} onClick={() => act(s.id, "close")} className="px-3 py-1 rounded-md bg-red-500/80 text-white text-xs font-bold">Close</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <input data-testid="season-name-input" placeholder="Name e.g. 2027/28" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="flex-1 bg-[#070707] border border-[#14110a] rounded-md px-3 py-1.5 text-sm text-slate-200" />
          <input data-testid="season-code-input" placeholder="Code e.g. 2027-28" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-40 bg-[#070707] border border-[#14110a] rounded-md px-3 py-1.5 text-sm text-slate-200" />
          <button data-testid="create-season-btn" onClick={create} className="px-4 py-1.5 rounded-md bg-[#E5B842] text-[#000000] text-sm font-bold">Create</button>
        </div>
      </section>

      <section className="rounded-xl border border-[#2b2512] bg-[#0a0a0a] p-4 mb-6">
        <h2 className="text-lg font-bold text-slate-100 mb-3">Tier configuration (versioned)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-tiers">
            <thead className="text-slate-400 text-xs uppercase">
              <tr><th className="px-2 py-1 text-left">Tier</th><th className="px-2 py-1 text-right">Champion</th><th className="px-2 py-1 text-right">Finalist</th><th className="px-2 py-1 text-right">SF</th><th className="px-2 py-1 text-right">QF</th><th className="px-2 py-1 text-right">R16</th><th className="px-2 py-1 text-right">Rules</th></tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.tier_code} className="border-t border-[#121212]">
                  <td className="px-2 py-1 font-mono text-[#E5B842]">{t.tier_code}</td>
                  <td className="px-2 py-1 text-right">{t.champion}</td><td className="px-2 py-1 text-right">{t.finalist}</td>
                  <td className="px-2 py-1 text-right">{t.semifinal}</td><td className="px-2 py-1 text-right">{t.quarterfinal}</td>
                  <td className="px-2 py-1 text-right">{t.round16}</td><td className="px-2 py-1 text-right font-mono text-slate-500">v{t.rules_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-[#2b2512] bg-[#0a0a0a] p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-100">Player ledger (active season)</h2>
          <button data-testid="recalc-season-btn" onClick={recalc} className="px-3 py-1 rounded-md bg-[#161616] text-slate-200 text-xs font-bold">Recalculate season</button>
        </div>
        <div className="flex gap-2 mb-3">
          <input data-testid="ledger-search-input" placeholder="Search player (name / WSTF id)" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-[#070707] border border-[#14110a] rounded-md px-3 py-1.5 text-sm text-slate-200" />
          <button data-testid="ledger-search-btn" onClick={searchLedger} className="px-4 py-1.5 rounded-md bg-[#E5B842] text-[#000000] text-sm font-bold">Search</button>
        </div>
        <div className="space-y-1" data-testid="admin-ledger">
          {ledger.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-lg bg-[#070707] border border-[#14110a] px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="text-slate-100 truncate">{r.player?.display_name || r.player_id}</div>
                <div className="text-xs font-mono text-slate-500">{r.tournament_id} · {r.tier_code} · {r.placement_label || "—"} → {r.points_awarded} pts · {r.result_status}{r.counts_in_best8 ? ` · Best-8 #${r.best8_rank}` : ""} · rules v{r.rules_version}</div>
              </div>
              {r.result_status === "ACTIVE" && (
                <button data-testid={`revoke-${r.id}`} onClick={() => revoke(r.id)} className="px-2 py-1 rounded-md bg-red-500/70 text-white text-xs font-bold shrink-0">Revoke</button>
              )}
            </div>
          ))}
          {ledger.length === 0 && <div className="text-sm text-slate-500">No ledger rows loaded. Search to view.</div>}
        </div>
      </section>
    </Page>
  );
}
